// You can write more code here

import { MinigameCardData } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import {
  MINIGAME_CARD_SOUND_OPTIONS,
  MINIGAME_CORRECT_COLOUR,
  MINIGAME_INCORRECT_COLOUR,
  MINIGAME_NEUTRAL_COLOUR,
  MINIGAME_PURPLE_COLOUR,
} from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { HorizontalLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/HorizontalLayoutGroup';
import {
  DoPositiveFeedback,
  DoReinforcementFeedback,
  setBitmapColor,
  waitForMilliseconds,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import {
  getBackingFromWidth,
  getOutlineFromWidth,
} from '@/game/GameComponents/PhaserGame/Utils/StudentGameUtils';
import { createArticulatoryGesture } from '@/game/prefabs/StudentGame/ArticulatoryGesture';
import CardDropSite from '@/game/prefabs/StudentGame/CardDropSite';
import Minigame from '@/game/scenes/Minigame';

/* START OF COMPILED CODE */

export default class StudentGameCard extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    // minigameCardBacking
    const minigameCardBacking = scene.add.image(0, 0, 'Card_48');
    minigameCardBacking.scaleX = 1.5;
    minigameCardBacking.scaleY = 1.5;
    this.add(minigameCardBacking);

    // outline
    const outline = scene.add.image(0, 0, 'Card-Highlight-48');
    outline.scaleX = 1.5;
    outline.scaleY = 1.5;
    this.add(outline);

    // minigameCardText
    const minigameCardText = scene.add.bitmapText(0, 0, 'Aronui', 'A');
    minigameCardText.setOrigin(0.5, 0.5);
    minigameCardText.tintTopLeft = 6896829;
    minigameCardText.tintTopRight = 6896829;
    minigameCardText.tintBottomLeft = 6896829;
    minigameCardText.tintBottomRight = 6896829;
    minigameCardText.text = 'A';
    minigameCardText.fontSize = -42;
    this.add(minigameCardText);

    // articulatoryGestureContainer
    const articulatoryGestureContainer = scene.add.container(0, -53);
    articulatoryGestureContainer.scaleX = 0.25;
    articulatoryGestureContainer.scaleY = 0.25;
    this.add(articulatoryGestureContainer);

    // articulatoryGesturesLayoutGroupContainer
    const articulatoryGesturesLayoutGroupContainer = scene.add.container(0, 0);
    articulatoryGestureContainer.add(articulatoryGesturesLayoutGroupContainer);

    this.minigameCardBacking = minigameCardBacking;
    this.outline = outline;
    this.minigameCardText = minigameCardText;
    this.articulatoryGesturesLayoutGroupContainer =
      articulatoryGesturesLayoutGroupContainer;
    this.articulatoryGestureContainer = articulatoryGestureContainer;

    /* START-USER-CTR-CODE */
    this.initialize();
    this.articulatoryGestureContainer.setVisible(false);
    /* END-USER-CTR-CODE */
  }

  private minigameCardBacking: Phaser.GameObjects.Image;
  private outline: Phaser.GameObjects.Image;
  private minigameCardText: Phaser.GameObjects.BitmapText;
  private articulatoryGesturesLayoutGroupContainer: Phaser.GameObjects.Container;
  private articulatoryGestureContainer: Phaser.GameObjects.Container;

  /* START-USER-CODE */
  minigame?: Minigame;
  origin?: {
    dropSite: CardDropSite;
    index: number;
    desiredPosition?: { x: number; y: number };
  };
  cardData?: MinigameCardData;
  isLocked?: boolean = false;
  get cardText() {
    return this.minigameCardText.text;
  }
  targetScale: number = 1;
  articulatoryGesturesLayoutGroup?: HorizontalLayoutGroup;
  MINIGAME_HEIGHT_BREAKPOINT: number = 1024;
  hasInitializedDragAndDrop: boolean = false;
  hasInitializedClickToPlace: boolean = false;
  anticipationTween?: Phaser.Tweens.Tween;

  onStartedDragging?: (
    StudentGameCard: StudentGameCard,
    pointer: any,
    dragX: number,
    dragY: number,
  ) => void;
  onDragged?: (pointer: any, dragX: number, dragY: number) => void;
  onEndedDragging?: (
    StudentGameCard: StudentGameCard,
    pointer: any,
    dragX: number,
    dragY: number,
  ) => void;
  onReturnedToHand?: (StudentGameCard: StudentGameCard) => void;
  onClicked?: (StudentGameCard: StudentGameCard) => void;

  initialize() {
    this.outline.setVisible(false);
    // scene: Phaser.Scene;
    // elements: (Phaser.GameObjects.Container | Phaser.GameObjects.Image)[];
    // columns: number;
    // hPadding: number;
    // vPadding: number;
    // hMargin?: number;
    // vMargin?: number;
    // backingElement?: Phaser.GameObjects.NineSlice | Phaser.GameObjects.Image;
    // alignment?: 'LEFT' | 'CENTER';

    this.articulatoryGesturesLayoutGroup = new HorizontalLayoutGroup({
      parent: this.articulatoryGesturesLayoutGroupContainer,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 40,
      align: 'center',
    });
  }

  setTargetScale(scale: number) {
    this.targetScale = scale;
    this.setScale(scale);
  }

  setupClickToPlaceFunctionality() {
    this.setInteractive();
    this.on('pointerdown', () => {
      this.onClicked?.(this);
    });
    this.hasInitializedClickToPlace = true;
  }

  setupDragAndDropFunctionality() {
    const transformedPoint = this.minigameCardBacking
      .getWorldTransformMatrix()
      .transformPoint(0, 0);

    const width = this.minigameCardBacking.displayWidth;
    const height = this.minigameCardBacking.displayHeight;
    this.setInteractive(
      new Phaser.Geom.Rectangle(
        transformedPoint.x - width / 2,
        transformedPoint.y - height / 2,
        width,
        height,
      ),
      Phaser.Geom.Rectangle.Contains,
    );
    this.setData('isDraggable', true);
    this.scene.input.setDraggable(this);
    //this.minigameCardBacking.setInteractive({ draggable: true });

    this.on('dragstart', (pointer: any) => {
      this.onStartedDragging?.(this, pointer, this.x, this.y);
    });

    this.on('drag', (pointer: any, dragX: number, dragY: number) => {
      this.onDragged?.(pointer, dragX, dragY);
    });

    this.on('dragend', (pointer: any, dragX: number, dragY: number) => {
      // Notify the minigame that the drag has ended.
      this.onEndedDragging?.(this, pointer, dragX, dragY);
    });

    this.on('pointerover', () => {
      PhaserAudioManager.playSFX(
        this.scene,
        'card-over',
        MINIGAME_CARD_SOUND_OPTIONS,
      );
      this.scene.tweens.add({
        targets: this,
        ease: 'Cubic.easeOut',
        scaleX: this.targetScale * 1.1,
        scaleY: this.targetScale * 1.1,
        duration: 150,
      });
    });

    this.on('pointerout', () => {
      this.scene.tweens.add({
        targets: this,
        ease: 'Cubic.easeOut',
        scaleX: this.targetScale,
        scaleY: this.targetScale,
        duration: 150,
      });
    });
    this.hasInitializedDragAndDrop = true;
  }

  defaultDragEndBehaviour() {
    this.setDepth(0);
    this.disableInteractive();
    this.scene.tweens.killTweensOf(this);
    if (this.origin) {
      const dropSiteTransform = this.origin.dropSite.getLocalTransformMatrix();
      const fromPos = { x: this.x, y: this.y };
      if (this.origin.dropSite) {
        this.setPosition(fromPos.x, fromPos.y);
      } else {
        this.setPosition(
          dropSiteTransform.tx + fromPos.x,
          dropSiteTransform.ty + fromPos.y,
        );
      }
      this.origin?.dropSite.addCard(this, this.origin.index);

      this.setInteractive();
      this.setScale(this.targetScale);
      //this.onReturnedToHand?.(this);
      return;
    }
  }

  setCardText(text: string) {
    this.minigameCardText.setText(text);
    // Resize the card to fit the text.
    const backingSizeX = Math.max(48, this.minigameCardText.width + 12);
    const backingTex = getBackingFromWidth(
      backingSizeX,
      this.isLocked ?? false,
    );
    const outlineTex = getOutlineFromWidth(backingSizeX);

    this.minigameCardBacking.setTexture(backingTex);
    this.outline.setTexture(outlineTex);
    if (!this.hasInitializedDragAndDrop) {
      this.setupDragAndDropFunctionality();
    }
    if (!this.hasInitializedClickToPlace) {
      this.setupClickToPlaceFunctionality();
    }
  }

  async showCorrectFeedback(feedbackDuration: number = 300): Promise<void> {
    this.scene.tweens.killTweensOf(this.outline);
    this.outline.tint = MINIGAME_CORRECT_COLOUR;
    this.outline.setVisible(true);
    this.outline.setAlpha(0);
    const originDepth = this.depth;
    this.setDepth(999);
    this.scene.tweens.add({
      targets: this.outline,
      alpha: 1,
      duration: feedbackDuration,
      ease: 'Cubic.easeOut',
      yoyo: true,
    });

    DoPositiveFeedback(this.scene, this, { feedbackDuration });

    await waitForMilliseconds(this.scene.time, feedbackDuration);
    this.setDepth(originDepth);
  }

  async showIncorrectFeedback(): Promise<void> {
    this.scene.tweens.killTweensOf(this.outline);
    this.outline.tint = MINIGAME_INCORRECT_COLOUR;
    this.outline.setVisible(true);
    this.outline.setAlpha(0);
    const feedbackDuration = 300;
    this.scene.tweens.add({
      targets: this.outline,
      alpha: 1,
      duration: feedbackDuration,
      ease: 'Cubic.easeOut',
      yoyo: true,
    });
    DoReinforcementFeedback(this.scene, this);
    await waitForMilliseconds(this.scene.time, feedbackDuration);
  }

  async showNeutralFeedback(
    feedbackDuration?: number,
    indefinitely?: boolean,
  ): Promise<void> {
    const duration = feedbackDuration ?? 400;
    this.scene.tweens.killTweensOf(this.outline);
    this.outline.tint = MINIGAME_NEUTRAL_COLOUR;
    this.outline.setVisible(true);
    this.outline.setAlpha(0);
    this.scene.tweens.add({
      targets: this.outline,
      alpha: 1,
      duration: duration,
      ease: 'Cubic.easeOut',
      yoyo: indefinitely ? false : true,
    });
    await waitForMilliseconds(this.scene.time, duration);
  }

  // Similar to neutral feedback, but keeps glowing in and out.
  showGuidance() {
    this.scene.tweens.killTweensOf(this.outline);
    this.outline.tint = MINIGAME_NEUTRAL_COLOUR;
    this.outline.setVisible(true);
    this.outline.setAlpha(0);
    this.scene.tweens.add({
      targets: this.outline,
      alpha: 1,
      duration: 1000,
      ease: 'Cubic.easeOut',
      yoyo: true,
      repeat: -1,
    });
  }

  async showPurpleFeedback(
    feedbackDuration?: number,
    indefinitely?: boolean,
  ): Promise<void> {
    const duration = feedbackDuration ?? 400;
    this.scene.tweens.killTweensOf(this.outline);
    this.outline.tint = MINIGAME_PURPLE_COLOUR;
    this.outline.setVisible(true);
    this.outline.setAlpha(0);
    this.scene.tweens.add({
      targets: this.outline,
      alpha: 1,
      duration: duration,
      ease: 'Cubic.easeOut',
      yoyo: indefinitely ? false : true,
    });
    await waitForMilliseconds(this.scene.time, duration);
  }

  doFadeOut(duration: number = 200) {
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration,
      ease: 'Cubic.easeOut',
    });
  }

  makeInteractive(interactive: boolean, updateDisplay: boolean = true) {
    if (this.isLocked) {
      return;
    }
    if (interactive) {
      this.setInteractive();
      if (updateDisplay) {
        this.setAlpha(1);
      }
    } else {
      this.disableInteractive();
      if (updateDisplay) {
        this.setAlpha(0.5);
      }
    }
  }

  // Lock the card in place, making it a static part of the scene.
  lockCard() {
    this.isLocked = true;
    this.setInteractive(false);
    this.disableInteractive();
    this.scene.tweens.killTweensOf(this);
    this.setDepth(0);
    this.minigameCardBacking.setTexture('Card-Locked');
    setBitmapColor(this.minigameCardText, 0xfaf9e1);
    this.minigameCardBacking.setTexture(
      getBackingFromWidth(Math.max(48, this.minigameCardText.width + 12), true),
    );
  }

  getDesiredPositionWorld() {
    if (this.origin?.desiredPosition) {
      return this.origin.desiredPosition;
    }
    return { x: this.x, y: this.y };
  }

  async showArticulatoryGesture(gesture?: string) {
    const worldX = this.getWorldTransformMatrix().tx;
    const worldY = this.getWorldTransformMatrix().ty;
    await createArticulatoryGesture(this.scene, worldX, worldY - 100, gesture);
  }

  setAnticipation(isAnticipating: boolean) {
    const intensity = 2;
    const duration = 100;
    if (isAnticipating) {
      if (this.anticipationTween) return;
      this.showNeutralFeedback(undefined, true);
      this.anticipationTween = this.scene.tweens.add({
        targets: this,
        x: this.x + intensity,
        duration: duration,
        ease: 'Power2.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    } else {
      this.outline.setVisible(false);
      this.anticipationTween?.stop();
      this.anticipationTween = undefined;
    }
  }

  clearOutline() {
    this.scene.tweens.killTweensOf(this.outline);
    this.outline.setAlpha(0);
    this.outline.setVisible(false);
  }

  getElementSize() {
    return new Phaser.Math.Vector2(
      this.minigameCardBacking.displayWidth * this.targetScale,
      this.minigameCardBacking.displayHeight * this.targetScale,
    );
  }

  destroy() {
    super.destroy();
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
